// app/shared/analytics/scheduleOnceDispatch.ts

const defaultDispatched = new Set<string>();

/**
 * Schedule a one-shot delayed dispatch that is safe under React Strict Mode.
 *
 * The key is recorded only when the timer fires, so cleanup can cancel a
 * pending send and the next setup will reschedule. After dispatch starts,
 * the same key is not scheduled again.
 *
 * Scope matters: callers that track a PAGE VIEW must pass their own per-mount
 * `dispatched` set. The shared default set lives as long as the tab, so a
 * session-stable key (session id, visitor id) in it silently drops every
 * re-visit, for example a reader pressing Back to an article they already
 * opened. Strict Mode does not need the set at all, because the cleanup
 * cancels the pending timer before it can fire.
 */
export function scheduleOnceDispatch(
  key: string,
  delayMs: number,
  dispatch: () => void,
  dispatched: Set<string> = defaultDispatched,
): () => void {
  if (dispatched.has(key)) {
    return () => {};
  }

  const timeoutId = setTimeout(() => {
    if (dispatched.has(key)) return;
    dispatched.add(key);
    dispatch();
  }, delayMs);

  return () => {
    clearTimeout(timeoutId);
  };
}

export function resetScheduleOnceDispatchForTests(
  dispatched: Set<string> = defaultDispatched,
): void {
  dispatched.clear();
}
